import { useState } from 'react'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { AuthShell } from './AuthShell'
import type { TotpSetupState, TotpVerifyState } from './TotpPage'

/**
 * Forma qiymatlari uchun sxema.
 *
 * antd `rules` FOYDALANUVCHIGA xabar ko'rsatadi, zod esa TIPNI kafolatlaydi.
 *
 * ⚠️ `tenantCode` MAYDONI YO'Q: operator faqat login va parolni biladi,
 * qaysi tashkilotga tegishli ekanini backend aniqlaydi (`authApi.ts`).
 */
const loginSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface LocationState {
  from?: { pathname?: string }
}

export function LoginPage() {
  const { t } = useTranslation()
  const { isAuthenticated, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Kirgan foydalanuvchi login sahifasini ochsa — orqaga qaytariladi
  if (isAuthenticated) return <Navigate to="/" replace />

  const handleFinish = (values: unknown) => {
    const parsed = loginSchema.safeParse(values)
    if (!parsed.success) return

    setSubmitting(true)
    setError(null)

    const state = location.state as LocationState | null
    const destination = state?.from?.pathname ?? '/'

    // Login endi hech qachon shu yerda tugamaydi — TOTP tenantdan mustaqil
    // va MAJBURIY, ya'ni natija doim ikkitadan biri bo'ladi. Tashkilot
    // tanlash (agar kerak bo'lsa) TOTP'dan KEYIN, `TotpPage` ichida hal
    // qilinadi.
    void signIn(parsed.data.username, parsed.data.password)
      .then((outcome) => {
        switch (outcome.kind) {
          case 'totpSetup': {
            const nextState: TotpSetupState = {
              stage: 'setup',
              setupToken: outcome.setupToken,
              qrUri: outcome.qrUri,
              secret: outcome.secret,
              from: destination,
            }
            navigate('/login/totp', { state: nextState })
            return
          }

          case 'totpRequired': {
            const nextState: TotpVerifyState = {
              stage: 'verify',
              totpToken: outcome.totpToken,
              from: destination,
            }
            navigate('/login/totp', { state: nextState })
          }
        }
      })
      .catch((err: unknown) => {
        setError(translateError(err, t))
      })
      .finally(() => setSubmitting(false))
  }

  return (
    /*
      ⚠️ Sarlavha ENDI 'X-SIB' EMAS, 'Kirish'.

      Keng ekranda mahsulot nomi chap ustunda katta harflar bilan turadi
      va kartada takrorlanishi ma'nosiz edi — bir ekranda ikkita bir xil
      sarlavha. Kartaning vazifasi boshqa: bu yerda NIMA qilinishini
      aytish.
    */
    <AuthShell title={t('auth.title')} subtitle={t('auth.subtitle')}>
      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Form<LoginFormValues>
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
        disabled={submitting}
      >
        <Form.Item
          name="username"
          label={t('auth.username')}
          rules={[{ required: true, message: t('operation.validation.usernameRequired') }]}
        >
          <Input prefix={<UserOutlined />} autoFocus autoComplete="username" />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('auth.password')}
          rules={[{ required: true, message: t('operation.validation.passwordRequired') }]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
        </Form.Item>

        <Button type="primary" htmlType="submit" block loading={submitting}>
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </Form>
    </AuthShell>
  )
}
